import React, { Suspense, lazy, useLayoutEffect } from 'react';
import { createBrowserRouter, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import RootLayout from '@/components/RootLayout';
import Spinner from '@/components/Spinner/Spinner';
import WelcomeScreen from '@/components/WelcomeScreen/WelcomeScreen';
import {
    LOGGED_OUT_MOBILE_EXPLORE_SESSION_KEY,
    SHOW_LOGGED_OUT_EXPLORE_QUERY,
    isLoggedOutNativeWelcomeEligible,
    isLoggedOutMobileExplorePersisted,
} from '@/constants/loggedOutHomeQuery';
import { useAuth } from '@/contexts';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { isNativePlatform } from '@/lib/platform';

// Lazily-loaded page components — each is its own JS chunk
const Explore = lazy(() => import('@/components/pages/Explore'));
const AdminPage = lazy(() => import('@/pages/admin/page'));
const AdvisorsPage = lazy(() => import('@/pages/advisors/page'));
const AdvisorProPage = lazy(() => import('@/pages/advisor-pro/page'));
const AdvisorProOnboardingPage = lazy(() => import('@/pages/advisor-pro/onboarding/page'));
const AdvisorProSuccessPage = lazy(() => import('@/pages/advisor-pro/success/page'));
const AdvisorProConnectReturnPage = lazy(() => import('@/pages/advisor-pro/connect/return/page'));
const AddDayPage = lazy(() => import('@/pages/add-day/page'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/callback/page'));
const BookingCompletePage = lazy(() => import('@/pages/booking/complete/page'));
const CommunityPage = lazy(() => import('@/pages/community/page'));
const CreatorsPage = lazy(() => import('@/pages/creators/page'));
const DestinationsPage = lazy(() => import('@/pages/destinations/page'));
const FaqPage = lazy(() => import('@/pages/faq/page'));
const ItinerariesPage = lazy(() => import('@/pages/itineraries/page'));
const ItineraryStaticPage = lazy(() => import('@/pages/itinerary/page'));
const ListNewPage = lazy(() => import('@/pages/list/new/page'));
const ListsPage = lazy(() => import('@/pages/lists/page'));
const LoginPage = lazy(() => import('@/pages/login/page'));
const NotificationsPage = lazy(() => import('@/pages/notifications/page'));
const OnboardingPage = lazy(() => import('@/pages/onboarding/page'));
const PlaceNewPage = lazy(() => import('@/pages/place/new/page'));
const PostStaticPage = lazy(() => import('@/pages/post/page'));
const ProfileStaticPage = lazy(() => import('@/pages/profile/page'));
const RedirectPage = lazy(() => import('@/pages/redirect/page'));
const ResultsPage = lazy(() => import('@/pages/results/page'));
const TravelStylesPage = lazy(() => import('@/pages/travel-styles/page'));
const TripsPage = lazy(() => import('@/pages/trips/page'));
const PerformancePage = lazy(() => import('@/pages/performance/page'));
const LinkPerformancePage = lazy(() => import('@/pages/performance/link/page'));
const RequestsPage = lazy(() => import('@/pages/requests/page'));
const RequestDetailsPage = lazy(() => import('@/pages/requests/[id]/page'));
const MyTravelPage = lazy(() => import('@/pages/my-travel/page'));
const SavedPage = lazy(() => import('@/pages/saved/page'));
const AllSavedPage = lazy(() => import('@/pages/saved/all/page'));
const SavedCountryPage = lazy(() => import('@/pages/saved/[country]/page'));
const SavedUsStatesPage = lazy(() => import('@/pages/saved/united-states/page'));
const SavedUsStatePage = lazy(() => import('@/pages/saved/united-states/[state]/page'));

// Dynamic-segment route components
const ItineraryRouteClient = lazy(() => import('@/pages/itinerary/[id]/ItineraryRouteClient'));
const ListRouteClient = lazy(() => import('@/pages/list/[id]/ListRouteClient'));
const PlaceRouteClient = lazy(() => import('@/pages/place/[id]/PlaceRouteClient'));
const PostMobileRedirect = lazy(() => import('@/pages/post/[id]/MobileRedirect'));
const DestinationsMobileRedirect = lazy(() => import('@/pages/destinations/[country]/MobileRedirect'));
const ShortShareMobileRedirect = lazy(() => import('@/pages/s/[code]/MobileRedirect'));
const ProfilePageClient = lazy(() => import('@/pages/profile/[id]/ProfilePageClient'));
const ProfileCityPageClient = lazy(() => import('@/pages/profile/[id]/city/[cityName]/ProfileCityPageClient'));
const RecommendationPageClient = lazy(() => import('@/pages/recommendation/[id]/RecommendationPageClient'));

const spinnerFallback = <Spinner />;

// Inline param-binding wrappers — keep route element fan-out close to the route table.
function PostByIdRoute() {
    const { id = '' } = useParams<{ id: string }>();
    return (
        <Suspense fallback={spinnerFallback}>
            <PostMobileRedirect id={id} />
        </Suspense>
    );
}

function DestinationsByCountryRoute() {
    const { country = '' } = useParams<{ country: string }>();
    return (
        <Suspense fallback={spinnerFallback}>
            <DestinationsMobileRedirect country={country} />
        </Suspense>
    );
}

function ShortShareRoute() {
    const { code = '' } = useParams<{ code: string }>();
    return (
        <Suspense fallback={spinnerFallback}>
            <ShortShareMobileRedirect code={code} />
        </Suspense>
    );
}

function ProfileByIdRoute() {
    const { id = '' } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const rawToken = searchParams.get('token') ?? undefined;
    const sharedLocationId = searchParams.get('shared_location_id') ?? undefined;
    const sharedRecommendationId = searchParams.get('shared_recommendation_id') ?? undefined;
    const profileToken = sharedLocationId || sharedRecommendationId ? undefined : rawToken;
    const locationToken = sharedLocationId ? rawToken : undefined;
    const recommendationToken = sharedRecommendationId ? rawToken : undefined;
    return (
        <Suspense fallback={spinnerFallback}>
            <ProfilePageClient
                key={id}
                id={id}
                token={profileToken}
                locationToken={locationToken}
                recommendationToken={recommendationToken}
            />
        </Suspense>
    );
}

function ProfileCityByIdRoute() {
    const { id = '', cityName = '' } = useParams<{ id: string; cityName: string }>();
    const [searchParams] = useSearchParams();
    const countryName = searchParams.get('country') ?? undefined;
    return (
        <Suspense fallback={spinnerFallback}>
            <ProfileCityPageClient
                id={id}
                cityName={cityName}
                countryName={countryName}
            />
        </Suspense>
    );
}

function RecommendationByIdRoute() {
    const { id = '' } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    return (
        <Suspense fallback={spinnerFallback}>
            <RecommendationPageClient id={id} token={token ?? null} />
        </Suspense>
    );
}

function LoggedOutHomeRoute() {
    const { user } = useAuth();
    const isMobile = useIsMobileViewport();
    const isNative = isNativePlatform();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const showExploreFromQuery = searchParams.get(SHOW_LOGGED_OUT_EXPLORE_QUERY) === 'true';
    const isLoggedOutNativeWelcome = isLoggedOutNativeWelcomeEligible({
        hasUser: !!user,
        isMobileViewport: isMobile,
        isNativePlatform: isNative,
    });

    useLayoutEffect(() => {
        if (isLoggedOutNativeWelcome && showExploreFromQuery && typeof window !== 'undefined') {
            try {
                sessionStorage.setItem(LOGGED_OUT_MOBILE_EXPLORE_SESSION_KEY, '1');
            } catch {
                /* ignore quota / private mode */
            }
        }
    }, [isLoggedOutNativeWelcome, showExploreFromQuery]);

    const persistExplore = isLoggedOutMobileExplorePersisted();
    const showMobileWelcome =
        isLoggedOutNativeWelcome && !persistExplore && !showExploreFromQuery;

    if (showMobileWelcome) {
        return (
            <WelcomeScreen
                onExplore={() => {
                    if (typeof window !== 'undefined') {
                        try {
                            sessionStorage.setItem(LOGGED_OUT_MOBILE_EXPLORE_SESSION_KEY, '1');
                        } catch {
                            /* ignore */
                        }
                    }
                    navigate(`/?${SHOW_LOGGED_OUT_EXPLORE_QUERY}=true`, { replace: false });
                }}
            />
        );
    }
    return (
        <Suspense fallback={spinnerFallback}>
            <Explore />
        </Suspense>
    );
}

function SavedCountryRoute() {
    const { country = '' } = useParams<{ country: string }>();
    return (
        <Suspense fallback={spinnerFallback}>
            <SavedCountryPage countryParam={country} />
        </Suspense>
    );
}

function SavedUsStateRoute() {
    const { state = '' } = useParams<{ state: string }>();
    return (
        <Suspense fallback={spinnerFallback}>
            <SavedUsStatePage stateParam={state} />
        </Suspense>
    );
}

/** Saved moved out of My Travel to its own top-level `/saved` tree — keep old
 *  bookmarked/shared `/my-travel/saved*` links working. */
function LegacyMyTravelSavedRedirect() {
    const { pathname, search } = useLocation();
    const target = pathname.replace(/^\/my-travel\/saved/, '/saved');
    return <Navigate to={`${target}${search}`} replace />;
}

function LazyRoute({ component: Component }: { component: React.ComponentType }) {
    return (
        <Suspense fallback={spinnerFallback}>
            <Component />
        </Suspense>
    );
}

export const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            { path: '/', element: <LoggedOutHomeRoute /> },
            { path: '/admin', element: <LazyRoute component={AdminPage} /> },
            { path: '/advisors', element: <LazyRoute component={AdvisorsPage} /> },
            { path: '/advisor-pro', element: <LazyRoute component={AdvisorProPage} /> },
            { path: '/advisor-pro/onboarding', element: <LazyRoute component={AdvisorProOnboardingPage} /> },
            { path: '/advisor-pro/success', element: <LazyRoute component={AdvisorProSuccessPage} /> },
            { path: '/advisor-pro/connect/return', element: <LazyRoute component={AdvisorProConnectReturnPage} /> },
            { path: '/add-day', element: <LazyRoute component={AddDayPage} /> },
            { path: '/auth/callback', element: <LazyRoute component={AuthCallbackPage} /> },
            { path: '/booking/complete', element: <LazyRoute component={BookingCompletePage} /> },
            { path: '/community', element: <LazyRoute component={CommunityPage} /> },
            { path: '/creators', element: <LazyRoute component={CreatorsPage} /> },
            { path: '/destinations', element: <LazyRoute component={DestinationsPage} /> },
            { path: '/destinations/:country', element: <DestinationsByCountryRoute /> },
            { path: '/faq', element: <LazyRoute component={FaqPage} /> },
            { path: '/itineraries', element: <LazyRoute component={ItinerariesPage} /> },
            { path: '/itinerary', element: <LazyRoute component={ItineraryStaticPage} /> },
            { path: '/itinerary/:id', element: <LazyRoute component={ItineraryRouteClient} /> },
            { path: '/list/:id', element: <LazyRoute component={ListRouteClient} /> },
            { path: '/list/new', element: <LazyRoute component={ListNewPage} /> },
            { path: '/lists', element: <LazyRoute component={ListsPage} /> },
            { path: '/login', element: <LazyRoute component={LoginPage} /> },
            { path: '/notifications', element: <LazyRoute component={NotificationsPage} /> },
            { path: '/onboarding', element: <LazyRoute component={OnboardingPage} /> },
            { path: '/performance', element: <LazyRoute component={PerformancePage} /> },
            { path: '/performance/link', element: <LazyRoute component={LinkPerformancePage} /> },
            { path: '/requests', element: <LazyRoute component={RequestsPage} /> },
            { path: '/requests/:id', element: <LazyRoute component={RequestDetailsPage} /> },
            { path: '/my-travel', element: <Navigate to="/my-travel/lists" replace /> },
            { path: '/my-travel/lists', element: <LazyRoute component={MyTravelPage} /> },
            { path: '/my-travel/trips', element: <LazyRoute component={MyTravelPage} /> },
            { path: '/my-travel/saved', element: <LegacyMyTravelSavedRedirect /> },
            { path: '/my-travel/saved/*', element: <LegacyMyTravelSavedRedirect /> },
            { path: '/saved', element: <LazyRoute component={SavedPage} /> },
            { path: '/saved/all', element: <LazyRoute component={AllSavedPage} /> },
            // Match US state grid + drilldown BEFORE the generic `:country` route
            // so `united-states` doesn't fall through to the flat drilldown.
            { path: '/saved/united-states', element: <LazyRoute component={SavedUsStatesPage} /> },
            { path: '/saved/united-states/:state', element: <SavedUsStateRoute /> },
            { path: '/saved/:country', element: <SavedCountryRoute /> },
            { path: '/place/:id', element: <LazyRoute component={PlaceRouteClient} /> },
            { path: '/place/new', element: <LazyRoute component={PlaceNewPage} /> },
            { path: '/post', element: <LazyRoute component={PostStaticPage} /> },
            { path: '/post/:id', element: <PostByIdRoute /> },
            { path: '/profile', element: <LazyRoute component={ProfileStaticPage} /> },
            { path: '/profile/edit', element: <Navigate to="/profile" replace /> },
            { path: '/profile/:id/city/:cityName', element: <ProfileCityByIdRoute /> },
            { path: '/profile/:id', element: <ProfileByIdRoute /> },
            { path: '/storefront/:id/city/:cityName', element: <ProfileCityByIdRoute /> },
            { path: '/storefront/:id', element: <ProfileByIdRoute /> },
            { path: '/book/:id/city/:cityName', element: <ProfileCityByIdRoute /> },
            { path: '/book/:id', element: <ProfileByIdRoute /> },
            { path: '/recommendation/:id', element: <RecommendationByIdRoute /> },
            { path: '/redirect', element: <LazyRoute component={RedirectPage} /> },
            { path: '/results', element: <LazyRoute component={ResultsPage} /> },
            { path: '/s/:code', element: <ShortShareRoute /> },
            { path: '/travel-styles', element: <LazyRoute component={TravelStylesPage} /> },
            { path: '/trips', element: <LazyRoute component={TripsPage} /> },
            { path: '*', element: <Navigate to="/" replace /> },
        ],
    },
]);
